/**
 * Hero Section Component
 * Dynamic split-layout hero with compelling visuals
 */

import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, Target, TrendingUp, Users } from 'lucide-react';

export const Hero: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="relative min-h-screen bg-cream overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-primary-100/80 via-primary-50/40 to-transparent" />
      <div className="absolute top-20 right-20 w-72 h-72 bg-primary-400/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-40 w-96 h-96 bg-primary-300/15 rounded-full blur-3xl" />

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(to right, rgb(122, 114, 104) 1px, transparent 1px),
                          linear-gradient(to bottom, rgb(122, 114, 104) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="min-h-screen flex items-center py-20 pt-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center w-full">

            {/* Left Column - Content */}
            <div className="max-w-2xl">
              {/* Eyebrow */}
              <div
                className={`inline-flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-8 transition-all duration-700 ${
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <Target className="w-4 h-4" />
                <span>OKR Hub by HR4India</span>
              </div>

              {/* Main Headline */}
              <h1
                className={`font-display text-5xl md:text-6xl lg:text-7xl text-ink leading-[1.1] mb-6 transition-all duration-700 delay-100 ${
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                Turn Strategy
                <br />
                <span className="text-primary-500">into Execution</span>
              </h1>

              {/* Subtext */}
              <p
                className={`text-xl md:text-2xl text-ink-light leading-relaxed mb-8 transition-all duration-700 delay-200 ${
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                Most organizations are busy—but not aligned.
                <span className="text-ink font-medium"> OKR Hub </span>
                helps you build clarity, ownership, and execution rhythm.
              </p>

              {/* Value Props */}
              <div
                className={`flex flex-wrap gap-4 mb-10 transition-all duration-700 delay-300 ${
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                {['Consulting', 'Managed Services', 'Software'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-stone-200">
                    <CheckCircle className="w-4 h-4 text-success-500" />
                    <span className="text-ink font-medium">{item}</span>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div
                className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-[400ms] ${
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                {/* Primary CTA */}
                <button
                  onClick={() => scrollToSection('contact')}
                  className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-primary-500 rounded-xl overflow-hidden shadow-lg hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-primary-600 to-primary-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="relative flex items-center gap-2">
                    Book a Discovery Call
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>

                {/* Secondary CTA */}
                <button
                  onClick={() => scrollToSection('services')}
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-ink border-2 border-stone-300 rounded-xl hover:border-primary-400 hover:text-primary-600 hover:-translate-y-0.5 transition-all duration-300"
                >
                  See How It Works
                </button>
              </div>
            </div>

            {/* Right Column - Visual */}
            <div
              className={`relative transition-all duration-1000 delay-300 ${
                isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
            >
              {/* Main Card */}
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-stone-200">
                {/* Header */}
                <div className="flex items-center justify-between mb-8 pb-6 border-b border-stone-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl text-ink">Company OKRs</h3>
                      <p className="text-sm text-ink-muted">Q1 2025 Progress</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-display text-success-600">78%</div>
                    <div className="text-xs text-ink-muted">Overall</div>
                  </div>
                </div>

                {/* OKR Items */}
                <div className="space-y-4">
                  {/* Objective 1 */}
                  <div className="group p-4 bg-gradient-to-r from-stone-50 to-white rounded-xl border border-stone-100 hover:border-primary-200 hover:shadow-md transition-all duration-300">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-ink text-sm">Achieve Market Leadership</h4>
                          <p className="text-xs text-ink-muted">Sales & Marketing</p>
                        </div>
                      </div>
                      <span className="text-lg font-display text-success-600">92%</span>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-success-400 to-success-500 rounded-full" style={{ width: '92%' }} />
                    </div>
                  </div>

                  {/* Objective 2 */}
                  <div className="group p-4 bg-gradient-to-r from-stone-50 to-white rounded-xl border border-stone-100 hover:border-primary-200 hover:shadow-md transition-all duration-300">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Users className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-ink text-sm">Build High-Performance Culture</h4>
                          <p className="text-xs text-ink-muted">People & Culture</p>
                        </div>
                      </div>
                      <span className="text-lg font-display text-warning-600">68%</span>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-warning-400 to-warning-500 rounded-full" style={{ width: '68%' }} />
                    </div>
                  </div>

                  {/* Objective 3 */}
                  <div className="group p-4 bg-gradient-to-r from-stone-50 to-white rounded-xl border border-stone-100 hover:border-primary-200 hover:shadow-md transition-all duration-300">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <Target className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-ink text-sm">Launch Product V2.0</h4>
                          <p className="text-xs text-ink-muted">Product & Engineering</p>
                        </div>
                      </div>
                      <span className="text-lg font-display text-success-600">85%</span>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-success-400 to-success-500 rounded-full" style={{ width: '85%' }} />
                    </div>
                  </div>
                </div>

                {/* Footer Stats */}
                <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-stone-100">
                  <div className="text-center">
                    <div className="text-2xl font-display text-ink">12</div>
                    <div className="text-xs text-ink-muted">Objectives</div>
                  </div>
                  <div className="text-center border-x border-stone-100">
                    <div className="text-2xl font-display text-ink">36</div>
                    <div className="text-xs text-ink-muted">Key Results</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-display text-ink">8</div>
                    <div className="text-xs text-ink-muted">Teams</div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-success-500 text-white px-4 py-2 rounded-full shadow-lg text-sm font-semibold">
                Live Dashboard
              </div>

              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-stone-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-ink">Weekly Check-in</div>
                    <div className="text-xs text-success-600">Completed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <button
          onClick={() => scrollToSection('problem')}
          className="flex flex-col items-center gap-2 text-ink-muted hover:text-primary-500 transition-colors"
        >
          <span className="text-xs font-medium tracking-wider uppercase">Explore</span>
          <div className="w-6 h-10 border-2 border-current rounded-full flex justify-center p-2">
            <div className="w-1 h-2 bg-current rounded-full animate-bounce" />
          </div>
        </button>
      </div>
    </section>
  );
};
